import { useEffect, useState } from 'react';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Search, Video, X } from 'lucide-react';
import { getVimeoContent } from '@/lib/firebase';
import { ProgramData } from '@/pages/add-program';
import { ImageUpload } from '@/components/image-upload';
import { logger } from '@/lib/logger';

interface Step5AdditionalVideosProps {
  data: ProgramData;
  updateData: (updates: Partial<ProgramData>) => void;
  onValidationChange: (isValid: boolean) => void;
}

interface ContentItem {
  id: string;
  title: string;
  author?: string;
  duration?: number;
  mediaType: string;
  thumbnailUrl?: string;
  externalId: string;
  enabled: boolean;
}

export default function Step5AdditionalVideos({ data, updateData, onValidationChange }: Step5AdditionalVideosProps) {
  const [contentItems, setContentItems] = useState<ContentItem[]>([]);
  const [filteredItems, setFilteredItems] = useState<ContentItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [showInstructionSearch, setShowInstructionSearch] = useState(false);
  const [showConclusionSearch, setShowConclusionSearch] = useState(false);
  
  const [instructionVideo, setInstructionVideo] = useState<ContentItem | null>(null);
  const [conclusionVideo, setConclusionVideo] = useState<ContentItem | null>(null);

  // Load content items
  useEffect(() => {
    loadContent();
  }, []);

  // Set current selections if they exist in data
  useEffect(() => {
    if (data.instructionContentId && !instructionVideo) {
      const video = contentItems.find(item => item.id === data.instructionContentId);
      if (video) setInstructionVideo(video);
    }
    
    if (data.conclusionContentId && !conclusionVideo) {
      const video = contentItems.find(item => item.id === data.conclusionContentId);
      if (video) setConclusionVideo(video);
    }
  }, [data.instructionContentId, data.conclusionContentId, contentItems, instructionVideo, conclusionVideo]);

  const loadContent = async () => {
    setLoading(true);
    try {
      const items = await getVimeoContent() as unknown as ContentItem[];
      setContentItems(items);
    } catch (error) {
      logger.error('Error loading content:', error);
    } finally {
      setLoading(false);
    }
  };

  // Filter content items based on search
  useEffect(() => {
    let filtered = contentItems;

    if (searchTerm) {
      filtered = filtered.filter(item =>
        item.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.author?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    setFilteredItems(filtered);
  }, [contentItems, searchTerm]);

  // Validate step - banner image is required
  useEffect(() => {
    const isValid = Boolean(data.bannerUrl && data.bannerUrl.trim() !== '');
    onValidationChange(isValid);
  }, [data.bannerUrl, onValidationChange]);

  const selectInstructionVideo = (video: ContentItem) => {
    setInstructionVideo(video);
    setShowInstructionSearch(false);
    setSearchTerm('');
    
    updateData({
      instructionContentId: video.id,
      instructionExternalId: video.externalId,
    });
  };

  const selectConclusionVideo = (video: ContentItem) => {
    setConclusionVideo(video);
    setShowConclusionSearch(false);
    setSearchTerm('');
    
    updateData({
      conclusionContentId: video.id,
      conclusionExternalId: video.externalId,
    });
  };

  const clearInstructionVideo = () => {
    setInstructionVideo(null);
    updateData({
      instructionContentId: '',
      instructionExternalId: '',
    });
  };

  const clearConclusionVideo = () => {
    setConclusionVideo(null);
    updateData({
      conclusionContentId: '',
      conclusionExternalId: '',
    });
  };

  const renderVideoCard = (video: ContentItem, onSelect: (video: ContentItem) => void) => (
    <Card 
      key={video.id} 
      className="cursor-pointer hover:shadow-md transition-shadow"
      onClick={() => onSelect(video)}
    >
      <CardContent className="p-4">
        <div className="flex items-center space-x-3">
          <Video className="w-8 h-8 text-primary flex-shrink-0" />
          <div className="flex-1 min-w-0">
            <h4 className="font-medium text-slate-900 truncate">{video.title}</h4>
            <div className="flex items-center space-x-2 text-sm text-slate-500 mt-1">
              {video.author && <span>by {video.author}</span>}
              {video.duration && <span>{Math.round(video.duration)} min</span>}
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );

  const renderSelectedVideo = (
    video: ContentItem | null, 
    title: string, 
    onClear: () => void,
    testId: string
  ) => (
    <Card className="border-green-200 bg-green-50">
      <CardContent className="p-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <Video className="w-8 h-8 text-green-600 flex-shrink-0" />
            <div className="flex-1 min-w-0">
              <h4 className="font-medium text-green-900">{title}</h4>
              {video && (
                <>
                  <p className="text-green-800 font-medium truncate">{video.title}</p>
                  <div className="flex items-center space-x-2 text-sm text-green-600 mt-1">
                    {video.author && <span>by {video.author}</span>}
                    {video.duration && <span>{Math.round(video.duration)} min</span>}
                  </div>
                </>
              )}
            </div>
          </div>
          {video && (
            <Button
              variant="ghost"
              size="sm"
              onClick={onClear}
              className="text-green-600 hover:text-green-700 hover:bg-green-100"
              data-testid={testId}
            >
              <X className="w-4 h-4" />
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  );

  return (
    <div className="space-y-6">
      {/* Instructions */}
      <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
        <h4 className="font-medium text-blue-900 mb-2">Program Banner & Additional Videos</h4>
        <p className="text-sm text-blue-800">
          Upload a banner image for your program (required). Instruction and conclusion videos are optional 
          and can be added to provide context at the beginning and end of your program.
        </p>
      </div>

      {/* Banner Image Upload - Required */}
      <div className="space-y-3">
        <Label className="text-sm font-medium text-slate-700">
          Program Banner Image <span className="text-red-500">*</span>
        </Label>
        <ImageUpload
          value={data.bannerUrl}
          onChange={(url) => updateData({ bannerUrl: url })}
          label="Banner Image"
          required={true}
          uploadType="program"
          data-testid="banner-image-upload"
        />
        {!data.bannerUrl && (
          <p className="text-sm text-slate-600">
            Please upload a banner image for your program. This image will be used to represent your program.
          </p>
        )}
      </div>

      {/* Instruction Video */}
      <div className="space-y-3">
        <Label className="text-sm font-medium text-slate-700">
          Instruction Video (Optional)
        </Label>
        
        {instructionVideo ? (
          renderSelectedVideo(
            instructionVideo, 
            "Selected Instruction Video", 
            clearInstructionVideo,
            "button-clear-instruction"
          )
        ) : (
          <Card className="border-dashed border-slate-300">
            <CardContent className="p-6 text-center">
              <Video className="w-12 h-12 text-slate-400 mx-auto mb-3" />
              <h4 className="font-medium text-slate-900 mb-2">No instruction video selected</h4>
              <Button
                variant="outline"
                onClick={() => setShowInstructionSearch(true)}
                data-testid="button-select-instruction-video"
              >
                Select Instruction Video
              </Button>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Conclusion Video */}
      <div className="space-y-3">
        <Label className="text-sm font-medium text-slate-700">
          Conclusion Video (Optional)
        </Label>
        
        {conclusionVideo ? (
          renderSelectedVideo(
            conclusionVideo, 
            "Selected Conclusion Video", 
            clearConclusionVideo,
            "button-clear-conclusion"
          )
        ) : (
          <Card className="border-dashed border-slate-300">
            <CardContent className="p-6 text-center">
              <Video className="w-12 h-12 text-slate-400 mx-auto mb-3" />
              <h4 className="font-medium text-slate-900 mb-2">No conclusion video selected</h4>
              <Button
                variant="outline"
                onClick={() => setShowConclusionSearch(true)}
                data-testid="button-select-conclusion-video"
              >
                Select Conclusion Video
              </Button>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Video Search Modal Content */}
      {(showInstructionSearch || showConclusionSearch) && (
        <Card className="border-primary">
          <CardHeader>
            <CardTitle className="flex items-center justify-between">
              <span>
                Select {showInstructionSearch ? 'Instruction' : 'Conclusion'} Video
              </span>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  setShowInstructionSearch(false);
                  setShowConclusionSearch(false);
                  setSearchTerm('');
                }}
              >
                <X className="w-4 h-4" />
              </Button>
            </CardTitle>
            <CardDescription>
              Search and select a video to use as the {showInstructionSearch ? 'instruction' : 'conclusion'} video.
            </CardDescription>
          </CardHeader>
          <CardContent>
            {/* Search Input */}
            <div className="mb-4">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  data-testid="input-video-search"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="Search videos..."
                  className="pl-10"
                />
              </div>
            </div>

            {/* Video List */}
            {loading ? (
              <div className="text-center py-8">
                <div className="animate-spin h-8 w-8 border-2 border-primary border-t-transparent rounded-full mx-auto mb-4"></div>
                <p className="text-slate-600">Loading videos...</p>
              </div>
            ) : filteredItems.length === 0 ? (
              <div className="text-center py-8">
                <Video className="w-12 h-12 text-slate-400 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-slate-900 mb-2">No videos found</h3>
                <p className="text-slate-600">Try adjusting your search terms.</p>
              </div>
            ) : (
              <div className="space-y-3 max-h-64 overflow-y-auto">
                {filteredItems.map((video) => 
                  renderVideoCard(
                    video, 
                    showInstructionSearch ? selectInstructionVideo : selectConclusionVideo
                  )
                )}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Summary */}
      <div className="p-4 bg-slate-50 rounded-lg">
        <h4 className="font-medium text-slate-900 mb-2">Step Summary</h4>
        <div className="space-y-2 text-sm">
          <div className="flex items-center justify-between">
            <span>Banner Image:</span>
            <Badge variant={data.bannerUrl ? "default" : "destructive"}>
              {data.bannerUrl ? "Uploaded" : "Required"}
            </Badge>
          </div>
          <div className="flex items-center justify-between">
            <span>Instruction Video:</span>
            <Badge variant={instructionVideo ? "default" : "secondary"}>
              {instructionVideo ? "Selected" : "None"}
            </Badge>
          </div>
          <div className="flex items-center justify-between">
            <span>Conclusion Video:</span>
            <Badge variant={conclusionVideo ? "default" : "secondary"}>
              {conclusionVideo ? "Selected" : "None"}
            </Badge>
          </div>
        </div>
      </div>
    </div>
  );
}

